import styled from '@emotion/styled';
import { Modal, Typography } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { TMenuItem } from '../../../types/common';
import { boolToText, textToBool } from '../../../utils/common';

type TProps = {
  menuItem: TMenuItem;
};

const Wrapper = styled('li')`
  height: 70px;
  min-width: 0;

  @media (max-width: 948px), (pointer: coarse) {
    height: 62px;
    width: 100%;
  }
`;

const Inner = styled(NavLink)<{
  active: 'true' | 'false';
}>`
  padding-inline: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  user-select: none;
  transition: background-color 0.3s ease;

  background-color: ${(props) =>
    textToBool(props.active) ? props.theme.COLORS.BLACK._300 : ''};

  svg path {
    fill: ${(props) =>
      textToBool(props.active) ? props.theme.COLORS.WHITE._100 : ''};
  }
  @media (max-width: 948px), (pointer: coarse) {
    flex-direction: column;
    justify-content: center;
    background-color: transparent;
    padding-inline: 8px;
    min-width: 0;

    svg path {
      fill: ${(props) => props.theme.COLORS.WHITE._100};
    }
`;

const Text = styled(Typography.Text)`
  color: ${(props) => props.theme.COLORS.WHITE._100};
  display: inline-block;
  font-size: 16px;

  @media (max-width: 948px), (pointer: coarse) {
    margin-left: 0;
    margin-top: 8px;
    font-size: 12px;
  }
  @media (max-width: 464px) {
    max-width: 100%;
    overflow: hidden;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const InnerDisabled = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  cursor: not-allowed;
  user-select: none;

  ${Text} {
    color: ${(props) => props.theme.COLORS.BLACK._700};
  }
  @media (max-width: 948px), (pointer: coarse) {
    flex-direction: column;
    justify-content: center;
    padding-inline: 8px;
    min-width: 0;
  }
`;

const IcoWrapper = styled('div')`
  width: 24px;
  height: 24px;

  svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const MenuItem: FC<TProps> = ({ menuItem }) => {
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const paths = location.pathname
      .split('/')
      .filter(Boolean)
      .map((el) => `/${el}`);

    if (paths.length === 0) {
      paths.push('/');
    }

    let result = false;

    if (menuItem) {
      if (menuItem.path === location.pathname) {
        result = true;
      }

      if (
        Array.isArray(menuItem.activePathTrigger) &&
        menuItem.activePathTrigger.some((el) => paths.includes(el))
      ) {
        result = true;
      }

      if (
        menuItem.activePathTrigger === location.pathname ||
        (Array.isArray(menuItem.activePathTrigger) &&
          menuItem.activePathTrigger.some((el) => location.pathname === el))
      ) {
        result = true;
      }
    }

    setIsActive(() => result);
  }, [location, menuItem]);

  return (
    <Wrapper>
      {menuItem.disabled ? (
        <InnerDisabled>
          <IcoWrapper>
            <menuItem.Icon />
          </IcoWrapper>
          <Text>{menuItem.text}</Text>
        </InnerDisabled>
      ) : (
        <Inner
          to={menuItem.path}
          active={boolToText(isActive)}
          onClick={(event) => {
            if (!menuItem.blockedModal) return;

            event.preventDefault();
            Modal.info({
              content: menuItem.blockedModal.content,
              okText: menuItem.blockedModal.okText,
              title: menuItem.blockedModal.title,
            });
          }}
        >
          <IcoWrapper>
            <menuItem.Icon />
          </IcoWrapper>
          <Text>{menuItem.text}</Text>
        </Inner>
      )}
    </Wrapper>
  );
};
